import { Platform } from 'react-native';
import {
  initConnection,
  endConnection,
  fetchProducts,
  requestPurchase,
  finishTransaction,
  purchaseUpdatedListener,
  purchaseErrorListener,
  restorePurchases,
  getAvailablePurchases,
  deepLinkToSubscriptions,
  type Purchase,
  type PurchaseIOS,
  type ProductSubscription,
  ErrorCode,
} from 'expo-iap';
import api from './api';
import { getApiErrorMessage } from '@/utils/api-error';

/** Apple IAP product IDs mapped to internal plan IDs. Configure via env vars. */
export const APPLE_IAP_PRODUCT_IDS: Record<string, string> = {
  basic: process.env.EXPO_PUBLIC_APPLE_IAP_PRODUCT_BASIC ?? '',
  professional: process.env.EXPO_PUBLIC_APPLE_IAP_PRODUCT_PROFESSIONAL ?? '',
  enterprise: process.env.EXPO_PUBLIC_APPLE_IAP_PRODUCT_ENTERPRISE ?? '',
};

/** Returns the Apple product SKU for a given internal plan ID, or null if not configured. */
export function getAppleProductId(planId: string): string | null {
  const id = APPLE_IAP_PRODUCT_IDS[planId];
  return id?.trim() ? id : null;
}

/** Returns true if Apple IAP is available and configured for this platform. */
export function isAppleIAPAvailable(): boolean {
  return Platform.OS === 'ios';
}

/** Initializes the IAP connection with the App Store. */
export async function initIAPConnection(): Promise<boolean> {
  try {
    return await initConnection();
  } catch {
    return false;
  }
}

/** Closes the IAP connection. */
export async function closeIAPConnection(): Promise<void> {
  try {
    await endConnection();
  } catch {
    // ignore
  }
}

/** Fetches Apple subscription products from the App Store. */
export async function fetchAppleSubscriptions(planIds: string[]): Promise<ProductSubscription[]> {
  const skus = planIds.map((id) => getAppleProductId(id)).filter(Boolean) as string[];

  if (!skus.length) return [];

  const result = await fetchProducts({ skus, type: 'subs' });
  return (result ?? []) as ProductSubscription[];
}

export interface AppleIAPPurchaseResult {
  ok: boolean;
  planId?: string;
  error?: string;
}

/** Validates an Apple IAP purchase with the backend and finishes the transaction. */
export async function validateApplePurchaseWithBackend(
  purchase: Purchase,
  planId: string,
): Promise<AppleIAPPurchaseResult> {
  const iosPurchase = purchase as PurchaseIOS;

  try {
    await api.post('/apple-iap/validate-purchase', {
      transactionId: iosPurchase.transactionId,
      originalTransactionId: iosPurchase.originalTransactionIdentifierIOS,
      productId: iosPurchase.productId,
      purchaseToken: iosPurchase.purchaseToken,
      planId,
    });

    await finishTransaction({ purchase, isConsumable: false });

    return { ok: true, planId };
  } catch (err: unknown) {
    const msg = getApiErrorMessage(err, 'Não foi possível confirmar a compra. Tente novamente.');
    return { ok: false, error: msg };
  }
}

/** Initiates a subscription purchase for the given plan. Result delivered via listener callbacks. */
export async function requestAppleSubscription(planId: string): Promise<void> {
  const sku = getAppleProductId(planId);
  if (!sku) {
    throw new Error(`Produto Apple IAP não configurado para o plano: ${planId}`);
  }

  await requestPurchase({
    type: 'subs',
    request: {
      apple: { sku },
    },
  });
}

/** Compra um produto direto pela SKU — os degraus da escada de equipe vêm da API, não do env. */
export async function requestAppleProduct(sku: string): Promise<void> {
  await requestPurchase({
    type: 'subs',
    request: {
      apple: { sku },
    },
  });
}

/**
 * Valida a compra de um degrau de equipe. A API confere
 * `fórmula(profissionais, clientes) ≤ preço do degrau`. Sem `selection`
 * (compra reentregue pelo StoreKit ou restaurada), ela mantém o contratado.
 */
export async function validateAppleTeamPurchaseWithBackend(
  purchase: Purchase,
  selection: { professionals: number; clients: number } | null,
): Promise<AppleIAPPurchaseResult> {
  const iosPurchase = purchase as PurchaseIOS;

  try {
    await api.post('/apple-iap/team/validate-purchase', {
      transactionId: iosPurchase.transactionId,
      originalTransactionId: iosPurchase.originalTransactionIdentifierIOS,
      productId: iosPurchase.productId,
      purchaseToken: iosPurchase.purchaseToken,
      ...(selection ?? {}),
    });

    await finishTransaction({ purchase, isConsumable: false });

    return { ok: true };
  } catch (err: unknown) {
    const msg = getApiErrorMessage(err, 'Não foi possível confirmar a compra. Tente novamente.');
    return { ok: false, error: msg };
  }
}

/** Reverse-looks-up the internal plan ID for an Apple product SKU. */
export function getPlanIdForProductId(productId: string): string | null {
  const entry = Object.entries(APPLE_IAP_PRODUCT_IDS).find(([, sku]) => sku === productId);
  return entry ? entry[0] : null;
}

/**
 * Restores previously purchased subscriptions from the App Store and re-validates
 * each one with the backend so entitlements are reinstated after a reinstall/device change.
 */
export async function restoreApplePurchases(): Promise<AppleIAPPurchaseResult[]> {
  await restorePurchases();
  const purchases = await getAvailablePurchases();

  const results: AppleIAPPurchaseResult[] = [];
  for (const purchase of purchases ?? []) {
    const planId = getPlanIdForProductId(purchase.productId);
    // Fora do mapa solo = degrau de equipe (a API recusa o que não conhece).
    results.push(
      planId
        ? await validateApplePurchaseWithBackend(purchase, planId)
        : await validateAppleTeamPurchaseWithBackend(purchase, null),
    );
  }

  return results;
}

/** Opens the native "manage subscriptions" screen (App Store on iOS). */
export async function openManageAppleSubscriptions(): Promise<void> {
  await deepLinkToSubscriptions();
}

export { purchaseUpdatedListener, purchaseErrorListener, ErrorCode };
